import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import type { IConversation } from '../../../extViewmodels/TextMessagingViewModel';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { apiErrorMessage } from './apiErrorMessage';
import { queryKeys } from './queryKeys';

export type CreateTextConversationVariables = {
	phoneNumberId: string;
	toE164: string;
};

/**
 * Creates the 1:1 conversation when the lookup found none. The server returns
 * the existing conversation for a matching pair, so a repeat is harmless; the
 * result is written straight into the lookup query's cache.
 */
export function useCreateTextConversation(): UseMutationResult<IConversation, Error, CreateTextConversationVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<IConversation, Error, CreateTextConversationVariables>({
		mutationFn: async ({ phoneNumberId, toE164 }) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<IConversation>(
				ImpersonationBroker.composeApiUrl({ urlPath: 'Conversation' }),
				'POST',
				{ phoneNumberId, toPhoneNumbers: [toE164] }
			);
			if (!result.success || !result.value) {
				throw asApiError(result);
			}
			return result.value;
		},
		onSuccess: (conversation, { phoneNumberId, toE164 }) => {
			queryClient.setQueryData(queryKeys.focusedView.textConversation(phoneNumberId, toE164), conversation);
		},
		onError: error => {
			toast.error(apiErrorMessage(error, 'Failed to start the text conversation.'));
		},
	});
}
